// AiConsoleIconRow — the right-edge icon trio that lets the operator
// toggle the AI Console drawer's view between topology / plan / closed.
//
// Mirrors Claude.ai's artifact-pane icon row: each icon is a small
// rounded-md button; the active mode glows accent; closed state is the
// dim default.
//
// Spec docs/specs/ai-mode-operations-console.md §4.5 drawer redesign
// (AM-5).
//
// Plan icon stays disabled in AM-5a — the drawer's plan mode lands in
// AM-5b. The component already accepts the `planSupported` prop so
// AM-5b only has to wire the data, no re-render of this file.

import React from "react";
import {
  Network, FileText, PanelRightClose, PanelRight,
} from "lucide-react";

/** AM-7 added "form" to host AI-emitted create forms in the same
 *  drawer slot as topology / plan. There's no IconRow button for
 *  "form" — operator can't toggle into it manually; only AI's
 *  `propose_drawer_form` opens it. To dismiss, operator uses the
 *  form's cancel button or the PanelRight toggle. */
export type DrawerView = "closed" | "topology" | "plan" | "form";

export interface AiConsoleIconRowProps {
  view: DrawerView;
  /** True when the AI session has a pending plan; the plan icon
   *  highlights with an accent dot to draw attention. */
  planPending?: boolean;
  /** AM-5b will wire — for AM-5a we receive but never use this branch. */
  planSupported?: boolean;
  onChange: (next: DrawerView) => void;
}

export default function AiConsoleIconRow({
  view, planPending = false, planSupported = false, onChange,
}: AiConsoleIconRowProps): React.ReactElement {
  return (
    <div
      role="group"
      aria-label="AI 導覽抽屜檢視切換"
      className="inline-flex items-center gap-0.5 rounded-md border border-border bg-bg-inset p-0.5"
    >
      <button
        type="button"
        onClick={() => onChange(view === "topology" ? "closed" : "topology")}
        aria-pressed={view === "topology"}
        aria-label="顯示結構圖"
        title="顯示結構圖"
        data-tour-id="console.drawer.topology"
        className={iconClass(view === "topology", false)}
      >
        <Network className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={planSupported ? () => onChange(view === "plan" ? "closed" : "plan") : undefined}
        disabled={!planSupported}
        aria-pressed={view === "plan"}
        aria-label="顯示待確認計畫"
        title={planSupported ? "顯示待確認計畫" : "目前無待確認計畫"}
        data-tour-id="console.drawer.plan"
        className={iconClass(view === "plan", !planSupported)}
      >
        <span className="relative inline-flex">
          <FileText className="h-3.5 w-3.5" />
          {planPending && (
            <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-accent" />
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onChange(view === "closed" ? "topology" : "closed")}
        aria-pressed={view !== "closed"}
        aria-label={view === "closed" ? "展開抽屜" : "收起抽屜"}
        title={view === "closed" ? "展開抽屜" : "收起抽屜"}
        data-tour-id="console.drawer.toggle"
        className={iconClass(view !== "closed", false)}
      >
        {view === "closed"
          ? <PanelRight className="h-3.5 w-3.5" />
          : <PanelRightClose className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function iconClass(active: boolean, disabled: boolean): string {
  return `flex items-center justify-center h-7 w-7 rounded transition-colors ${
    active
      ? "bg-accent text-text-inverse shadow-sm"
      : disabled
        ? "text-text-muted/50 cursor-not-allowed"
        : "text-text-muted hover:text-text-primary hover:bg-bg-hover"
  }`;
}
